/**
 * The addresses an emailing alert goes to, as the reader types them.
 *
 * Pure, and React-free. The endpoint checks every address again before it
 * saves, and once more before each send, so this check only tells the reader
 * early which address it will refuse.
 */

/** The most addresses one alert emails. The endpoint's own limit. */
export const MAX_EMAIL_TO = 10

/** The domains an alert may email. The endpoint's own list. */
const DOMAINS: ReadonlySet<string> = new Set(['blytzpay.com', 'blytz.com'])

const ONE_ADDRESS = /^[^\s@,;]+@([^\s@,;]+)$/

/** The addresses, or the first thing wrong with what the reader typed. */
export type EmailToRead =
  { ok: true; addresses: string[] } | { ok: false; problem: string }

/**
 * Read the addresses the reader typed, split on commas, semicolons, spaces or new lines.
 *
 * @param text - What the field holds.
 * @returns The addresses in lower case, each once, or the problem. Blank reads as none.
 */
export function readEmailTo(text: string): EmailToRead {
  const parts = text
    .split(/[\s,;]+/)
    .map(part => part.trim().toLowerCase())
    .filter(part => part !== '')
  const addresses: string[] = []
  for (const part of parts) {
    const match = ONE_ADDRESS.exec(part)
    if (!match) {
      return { ok: false, problem: `${part} is not an email address.` }
    }
    if (!DOMAINS.has(match[1] ?? '')) {
      return {
        ok: false,
        problem: `${part}: alerts email only @blytzpay.com and @blytz.com addresses.`
      }
    }
    if (!addresses.includes(part)) {
      addresses.push(part)
    }
  }
  if (addresses.length > MAX_EMAIL_TO) {
    return {
      ok: false,
      problem: `An alert emails at most ${MAX_EMAIL_TO} addresses.`
    }
  }

  return { ok: true, addresses }
}
